import {
    Button,
    Card,
    CardActions,
    CardHeader,
    colors,
    Grid,
    InputAdornment,
    MenuItem,
    Modal,
    Switch,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useEffect, useRef, useState} from 'react';
import CardContent from '@material-ui/core/CardContent';
import getNexioToken from './getNexioToken.api';
import {T} from 'react-polyglot-hooks';
import {
    NexioPaymentTypes,
    PaymentStatus,
    PaymentType,
    PaymentTypes
} from '../../../graphql/payments.datatypes';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import getBillingParty from '../../BillingParty/getBillingParty.api';
import anyLoading from '../../../utils/anyLoading';
import createPayment, {CreatePaymentInput} from '../PaymentEditModal/createPayment.api';
import moment from 'moment';
import {useSnackbar} from 'notistack';
import {formatMoney} from '../../../utils/formatters';
import InfoPopover from '../../../components/InfoPopover';
import {BillingParty} from '../../../graphql/billing_party.datatypes';
import createFirstPayment from './createFirstPayment.api';
import createAutomaticPayment, {CreateAutomaticPaymentInput} from '../AutomaticPaymentCheckoutFrame/createAutomaticPayment.api';
import addLog from '../../../graphql/addLog.api';
import {WcvLogCodes, WcvLogLevel, WcvType} from '../../../graphql/logs.datatypes';
import {getMe} from '../../../graphql/auth';
import {FeatureContext} from '../../../App';
import {FeatureFlagSlug} from '../../../graphql/features';
import getStateByAbbreviation from '../../States/getStateByAbbreviation.api';
import processPayment from '../processPayment.api';
import {addCreditCardAdjustment, AddCreditCardAdjustmentInput} from './addCreditCardAdjustment.api';
import validateSurcharge from "../validateSurcharge.api";

const image = require('../assets/echecke.png');

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        outline: 'none',
        boxShadow: theme.shadows[20],
        width: 500,
        height: 575,
        maxHeight: '100%',
        overflowY: 'auto',
        maxWidth: '100%'
    },
    summaryTable: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    iframe: {
        border: 0,
        width: '100%',
        height: 400,
        marginTop: theme.spacing(1),
    },
    container: {
        marginTop: theme.spacing(0),
        paddingTop: 0,
        paddingBottom: 0,
    },
    actions: {
        position: 'absolute',
        right: 0,
        justifyContent: 'flex-end',
    },
    deleteButton: {
        color: theme.palette.white,
        backgroundColor: colors.red[600],
        '&:hover': {
            backgroundColor: colors.red[900]
        }
    },
    processingFeeMessage: {
        color: colors.red[600],
    },
    submitButton: {
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    image: {
        marginTop: 50,
        display: 'inline-block',
        maxWidth: '100%',
        width: 560
    }
}));

const PaymentCheckoutFrame = (props: {
    onClose: () => void,
    onSuccess: () => void,
    open: boolean,
    billingParty?: BillingParty,
    billingPartyId?: number,
    billingPartyMemberId?: number,
    amount?: number,
    message?: string,
    isFirstPayment?: boolean
}) => {
    const {open, onClose, onSuccess, billingPartyId, message, isFirstPayment} = props;
    const classes = useStyles();
    const frameRef = useRef<HTMLIFrameElement>(null);
    const tokenRequestTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [frameUrl, setFrameUrl] = useState('');
    const [isNexioValid, setisNexioValid] = useState(false);
    const [paymentType, setPaymentType] = useState<number>(-1);
    const [amount, setAmount] = useState(props.amount ? props.amount : 0);
    const [nexioAmount, setNexioAmount] = useState(0);
    const [creditCardFee, setCreditCardFee] = useState(0);
    const [error, setError] = useState<string | undefined>();
    const [isRecoverableError, setIsRecoverableError] = useState(false);
    const [retryAttempts, setRetryAttempts] = useState(0);
    const [paymentProcessingAttempted, setPaymentProcessingAttempted] = useState(false);
    const [usedTokens, setUsedTokens] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [sendCreatePayment] = createPayment();
    const [sendCreateFirstPayment] = createFirstPayment();
    const [sendProcessPayment] = processPayment();
    const [sendValidateSurcharge] = validateSurcharge()
    const [sendAddCreditCardAdjustment] = addCreditCardAdjustment();
    const {enqueueSnackbar} = useSnackbar();
    const [processing, setProcessing] = useState(false);
    const [iframeLoadTimeout, setIframeLoadTimeout] = useState<NodeJS.Timeout | null>(null);
    const [paymentTimeoutId, setPaymentTimeoutId] = useState<NodeJS.Timeout | null>(null);
    const [cardTokenizationTimeoutId, setCardTokenizationTimeoutId] = useState<NodeJS.Timeout | null>(null);
    const billingPartyData = getBillingParty(billingPartyId ? billingPartyId : -1);
    const [billingPartyMemberId, setBillingPartyMemberId] = useState(props.billingPartyMemberId ? props.billingPartyMemberId : -1);
    const billingParty = props.billingParty ? props.billingParty : billingPartyData.data && billingPartyData.data.billingParty;

    // Unique session ID for tracking this payment flow
    const sessionId = useRef(`payment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    const paymentStartTime = useRef<number | null>(null);
    const payingMembers = billingParty && billingParty.payingMembers;
    const groupCreditCardFee = billingParty && billingParty.booking && billingParty.booking.group && billingParty.booking.group.credit_card_fee;

    const tokenData = getNexioToken(nexioAmount, paymentType, billingPartyMemberId);
    const nexioToken = tokenData.data && tokenData.data.getNexioToken;

    const meData = getMe();
    const [sendAddLog] = addLog(false);

    const loadingData = anyLoading(tokenData, billingPartyData);

    // Feature flags
    const features = React.useContext(FeatureContext);
    const automaticPaymentsFeature = features.find(f => f.slug === FeatureFlagSlug.AUTOMATIC_PAYMENTS)
    const automaticPaymentsEnabled = automaticPaymentsFeature && automaticPaymentsFeature.enabled;

    const [memberHasAutomaticPayment, setMemberHasAutomaticPayment] = useState(false);
    const [paymentFrequency, setPaymentFrequency] = useState(-1);
    const [paymentFrequencyError, setPaymentFrequencyError] = useState(false);
    const currentPayingMember = billingPartyMemberId && payingMembers && payingMembers.find((member) => member.id === billingPartyMemberId) || undefined;
    const autoPaymentEligible = automaticPaymentsEnabled && (isFirstPayment || (currentPayingMember && currentPayingMember.primary_paying_member && !currentPayingMember.hasAutomaticPayment));
    const [stateAbbr, setStateAbbr] = useState('');
    const [creditCardFeeTotal, setCreditCardFeeTotal] = useState(0);
    const stateData = getStateByAbbreviation(stateAbbr);
    const [processStep, setProcessStep] = useState(1);
    const [cardToken, setCardToken] = useState();
    const [surchargeAllowed, setSurchargeAllowed] = useState(false);

    const [sendCreateAutomaticPayment] = createAutomaticPayment();

    // Comprehensive logging function for payment flow tracking
    const logPaymentEvent = (eventType: string, details: any = {}, level = WcvLogLevel.INFO) => {
        if (!meData || !meData.data || !meData.data.me || !meData.data.me.id || !billingParty || !billingParty.id) {
            return;
        }

        const browserInfo = {
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
            cookieEnabled: navigator.cookieEnabled,
            onLine: navigator.onLine,
            screenWidth: screen.width,
            screenHeight: screen.height,
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
            referrer: document.referrer,
            timestamp: new Date().toISOString(),
            timeSinceStart: paymentStartTime.current ? Date.now() - paymentStartTime.current : null
        };

        const logData = {
            data: {
                level,
                user_id: meData.data.me.id,
                type: WcvType.PAYMENT,
                type_id: billingParty.id,
                code: WcvLogCodes.BILLING_PARTY_PAYMENT_FAILED,
                description: `Payment Flow Event: ${eventType} - Session: ${sessionId.current}`,
                payload: JSON.stringify({
                    sessionId: sessionId.current,
                    eventType,
                    billing_party_id: billingParty.id,
                    billing_party_member_id: billingPartyMemberId,
                    payment_type: paymentType === PaymentType.CARD ? 'Credit Card' : paymentType === PaymentType.ECHEQUE ? 'E-Check' : 'Unknown',
                    amount,
                    nexioAmount,
                    processStep,
                    loading,
                    processing,
                    isNexioValid,
                    error,
                    currentPayingMember: currentPayingMember ? {
                        id: currentPayingMember.id,
                        first_name: currentPayingMember.first_name,
                        last_name: currentPayingMember.last_name,
                        email: currentPayingMember.email
                    } : null,
                    browserInfo,
                    customDetails: details
                })
            }
        };

        sendAddLog({variables: logData})
            .then(() => {
                // Log sent successfully
            })
            .catch(() => {
                // Log failed to send
            });
    };

    // Extract stateFee so it can be included in dependencies
    const stateFee = stateData.data && stateData.data.stateByAbbreviation && stateData.data.stateByAbbreviation.credit_card_fee && parseFloat(stateData.data.stateByAbbreviation.credit_card_fee.fee)

    useEffect(() => {
        let fee = 0;
        if (paymentType === PaymentType.CARD && !stateData.loading && surchargeAllowed) {
            // if group fee is off, we don't want to charge a fee
            if (!(groupCreditCardFee && groupCreditCardFee > 0)) {
                setCreditCardFee(fee)
            }
            // if we have group fee but no state fee
            if ((groupCreditCardFee && groupCreditCardFee > 0) && (stateFee === undefined || stateFee === null)) {
                fee = groupCreditCardFee
                setCreditCardFee(groupCreditCardFee)
            }
            // when we have both fees, find which to use (state fee can be 0% and should still override)
            if ((groupCreditCardFee && groupCreditCardFee > 0) && (stateFee !== undefined && stateFee !== null)) {
                fee = stateFee < groupCreditCardFee ? stateFee : groupCreditCardFee;
                setCreditCardFee(fee)
            }

        }
        const feeTotal = Math.round(amount * (fee / 100) * 100) / 100;
        const total = amount + feeTotal
        setCreditCardFeeTotal(feeTotal);
        setNexioAmount(Math.round(total * 100) / 100)
    }, [stateData, paymentType, surchargeAllowed, groupCreditCardFee, amount, stateFee])

    useEffect(() => {
        if (tokenRequestTimeoutRef.current) {
            clearTimeout(tokenRequestTimeoutRef.current);
            tokenRequestTimeoutRef.current = null;
        }

        if (paymentType === -1 || nexioToken) {
            return;
        }

        const TOKEN_REQUEST_TIMEOUT_MS = getProgressiveTimeouts().cardTokenization;
        const timeoutId = setTimeout(() => {
            tokenRequestTimeoutRef.current = null;

            logDiagnosticInfo('TOKEN_REQUEST_TIMEOUT', {
                timeoutDuration: TOKEN_REQUEST_TIMEOUT_MS,
                paymentType: paymentType === PaymentType.CARD ? 'CARD' : paymentType === PaymentType.ECHEQUE ? 'ECHEQUE' : 'UNKNOWN',
                billingPartyMemberId,
                specificContext: 'Waiting for getNexioToken GraphQL response'
            });

            setPaymentType(-1);
            setFrameUrl('');
            setisNexioValid(false);
            setLoading(false);
            setError(getEscalatedErrorMessage('tokenRequest'));
            setIsRecoverableError(false);

            enqueueSnackbar(`Payment form setup timed out after ${TOKEN_REQUEST_TIMEOUT_MS / 1000} seconds. Please try again.`, {variant: 'error'});
        }, TOKEN_REQUEST_TIMEOUT_MS);

        tokenRequestTimeoutRef.current = timeoutId;

        return () => {
            if (tokenRequestTimeoutRef.current === timeoutId) {
                clearTimeout(tokenRequestTimeoutRef.current);
                tokenRequestTimeoutRef.current = null;
            }
        };
    }, [billingPartyMemberId, enqueueSnackbar, nexioToken, paymentType, retryAttempts]);

    useEffect(() => {
        if (nexioToken && tokenRequestTimeoutRef.current) {
            clearTimeout(tokenRequestTimeoutRef.current);
            tokenRequestTimeoutRef.current = null;
        }
    }, [nexioToken]);

    const validateCardForm = (data: any) => {
        return data.billToAddressOneValid &&
            data.billToAddressTwoValid &&
            data.billToCityValid &&
            data.billToCountryValid &&
            data.billToPostalValid &&
            data.billToStateValid &&
            data.cardExpirationValid &&
            data.cardHolderNameValid &&
            data.cardNumberValid &&
            data.cardSecurityCodeValid &&
            data.cardTypeValid;
    };

    const validateEchequeForm = (data: any) => {
        return data.accountHolderNameValid &&
            //data.accountNumberValid &&
            data.billToAddressOneValid &&
            data.billToAddressTwoValid &&
            data.billToCityValid &&
            data.billToCountryValid &&
            data.billToPostalValid &&
            data.billToStateValid &&
            data.routingNumberValid;
    };

    const sendAutoPaymentEnrollmentRequest = (payload: CreateAutomaticPaymentInput) => {
        if (!automaticPaymentsEnabled) {
            return;
        }
        if (!memberHasAutomaticPayment) {
            return;
        }
        sendCreateAutomaticPayment({variables: payload})
            .then(() => {
                setProcessing(false);
                enqueueSnackbar('Automatic Payment Saved.', {
                    variant: 'success'
                });
            })
            .catch(() => {
                setProcessing(false);
                enqueueSnackbar('Save Automatic Payment Failed!', {
                    variant: 'error'
                });
            });
    };

    const savePaymentData = (data: any) => {
        setProcessing(true);

        let remaining = nexioAmount;
        //assign payments.
        if (creditCardFeeTotal > 0) remaining -= creditCardFeeTotal;

        const members = billingParty!.members!;
        const minMemberAmount = Math.floor(remaining / members.length);
        const createPayments = members.map((member, index) => {
            let memberAmount = 0;
            if (index === members.length - 1) {
                memberAmount = remaining;
            } else {
                memberAmount = minMemberAmount
                remaining -= minMemberAmount;
            }
            return {
                group_member_id: member.group_member_id!,
                amount: memberAmount,
            };
        });

        const sanitisedData: CreatePaymentInput = {
            data: {
                billing_party_member_id: billingPartyMemberId,
                amount: nexioAmount,
                billing_party_amount: creditCardFeeTotal,
                timestamp: moment(data.transactionDate).format('YYYY-MM-DD HH:mm:ss'),
                payment_type: paymentType,
                payment_status: paymentType === PaymentType.CARD ? PaymentStatus.COMPLETE : PaymentStatus.PENDING,
                groupMembers: undefined,
                cc_fee: paymentType === PaymentType.CARD ? creditCardFeeTotal : null,

                provider_reference: data.gatewayResponse.refNumber,
                provider_status: data.transactionStatus,
                provider_response_raw: JSON.stringify(data),

                groupMemberPayments: {
                    create: createPayments,
                },
            }
        };

        const autoPayData: CreateAutomaticPaymentInput = {
            data: {
                billing_party_member_id: billingPartyMemberId,
                payment_type: paymentType,
                payment_token: data && data.token && data.token.token,
                last_four: data && data.token && data.token.lastFour,
                routing_number: data && data.bank && data.bank.routingNumber,
                payment_frequency: paymentFrequency
            }
        }

        if (isFirstPayment) {
            sendCreateFirstPayment({variables: sanitisedData})
                .then((response) => {
                    const paymentId = response && response.data && response.data.createFirstPayment && response.data.createFirstPayment.id;
                    enqueueSnackbar('Payment Saved.', {variant: 'success'});
                    sendAutoPaymentEnrollmentRequest(autoPayData);
                    if (paymentType === PaymentType.CARD && creditCardFeeTotal > 0) {
                        // @ts-ignore
                        handleAddCreditCardAdjustment(paymentId)
                    }
                    resetPaymentState();
                    onSuccess();
                })
                .catch(() => {
                    enqueueSnackbar('Save Payment Failed!', {variant: 'error'});
                    setProcessing(false);
                });
        } else {
            sendCreatePayment({variables: sanitisedData})
                .then((response) => {
                    const paymentId = response && response.data && response.data.createPayment && response.data.createPayment.id;
                    enqueueSnackbar('Payment Saved.', {variant: 'success'});
                    sendAutoPaymentEnrollmentRequest(autoPayData);
                    if (paymentType === PaymentType.CARD && creditCardFeeTotal > 0) {
                        // @ts-ignore
                        handleAddCreditCardAdjustment(paymentId)
                    }
                    resetPaymentState();
                    onSuccess();
                })
                .catch(() => {
                    enqueueSnackbar('Save Payment Failed!', {variant: 'error'});
                    setProcessing(false);
                });
        }
    };

    const storePaymentErrorLog = (data: any) => {
        const sanitisedData = {
            data: {
                level: WcvLogLevel.WARN,
                user_id: meData!.data!.me!.id,
                type: WcvType.PAYMENT,
                type_id: null,
                code: WcvLogCodes.BILLING_PARTY_PAYMENT_FAILED,
                description: `Payment failed for billing party ${billingParty!.id}.`,
                payload: JSON.stringify({
                    billing_party_id: billingParty!.id,
                    billing_party_member_id: billingPartyMemberId,
                    billing_party_member_first_name: currentPayingMember!.first_name,
                    billing_party_member_last_name: currentPayingMember!.last_name,
                    billing_party_member_email: currentPayingMember!.email,
                    payment_type: paymentType === PaymentType.CARD ? 'Credit Card' : paymentType === PaymentType.ECHEQUE ? 'E-Check' : 'Unknown',
                    amount: nexioAmount,
                    payment_error: data
                }),
            }
        };

        sendAddLog({variables: sanitisedData})
            .then(() => {})
            .catch(() => {});
    };

    useEffect(() => {
        const messageListener = (event: any) => {
            if (nexioToken) {
                // Allow anything from nexiopay.com and nexiopaysandbox.com as recommended by Nexio
                const isNexioOrigin = event.origin.endsWith('.nexiopay.com') ||
                    event.origin.endsWith('.nexiopaysandbox.com') ||
                    event.origin === 'https://nexiopay.com' ||
                    event.origin === 'https://nexiopaysandbox.com';

                // Only log critical payment events, not routine validation events
                const isCriticalEvent = event.data && event.data.event &&
                    ['loaded', 'submit', 'processed', 'eCheckProcessed', 'cardSaved', 'error'].includes(event.data.event);

                // Log unknown events from potential Nexio origins for debugging
                const isUnknownFromNexio = isNexioOrigin && event.data && event.data.event &&
                    !['loaded', 'formValidations', 'submit', 'processed', 'eCheckProcessed', 'cardSaved', 'error'].includes(event.data.event);

                if (isCriticalEvent || isUnknownFromNexio) {
                    console.log('IFRAME MESSAGE RECEIVED - Event:', event.data.event, 'Origin:', event.origin, 'PaymentType:', paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE');
                    logPaymentEvent('NEXIO_POST_MESSAGE_RECEIVED', {
                        origin: event.origin,
                        eventType: (event.data && event.data.event) || 'unknown',
                        paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                        isNexioOrigin,
                        sessionId: sessionId.current
                    });
                }

                if (!isNexioOrigin) {
                    // Only log rejections for critical events from wrong origins
                    if (isCriticalEvent) {
                        logPaymentEvent('NEXIO_POST_MESSAGE_ORIGIN_REJECTED', {
                            origin: event.origin,
                            eventType: (event.data && event.data.event) || 'unknown',
                            paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                            expectedOrigins: ['.nexiopay.com', '.nexiopaysandbox.com', 'https://nexiopay.com', 'https://nexiopaysandbox.com']
                        }, WcvLogLevel.WARN);
                    }
                    return;
                }

                if (isNexioOrigin) {
                    switch (event.data.event) {
                        case 'loaded':
                            console.log('IFRAME LOADED SUCCESSFULLY - PaymentType:', paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE', 'LoadTime:', paymentStartTime.current ? Date.now() - paymentStartTime.current + 'ms' : 'N/A');
                            logPaymentEvent('NEXIO_IFRAME_LOADED', {
                                paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                                frameUrl,
                                loadingTime: paymentStartTime.current ? Date.now() - paymentStartTime.current : null
                            });
                            setLoading(false);
                            setError(undefined); // Clear any previous errors or warnings
                            if (iframeLoadTimeout) {
                                clearTimeout(iframeLoadTimeout);
                                setIframeLoadTimeout(null);
                            }
                            break;
                        case 'formValidations':
                            const isValidCard = paymentType === PaymentType.CARD ? validateCardForm(event.data.data) : false;
                            const isValidEcheck = paymentType === PaymentType.ECHEQUE ? validateEchequeForm(event.data.data) : false;
                            const isValid = isValidCard || isValidEcheck;

                            // Only log final validation state changes, not every keystroke
                            if (isValid !== isNexioValid) {
                                logPaymentEvent('NEXIO_FORM_VALIDATION_CHANGED', {
                                    paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                                    isValid,
                                    previousValid: isNexioValid
                                });
                            }

                            if (paymentType === PaymentType.CARD) setisNexioValid(isValidCard);
                            if (paymentType === PaymentType.ECHEQUE) setisNexioValid(isValidEcheck);
                            break;
                        case 'submit':
                            logPaymentEvent('NEXIO_FORM_SUBMITTED', {
                                paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                                nexioAmount,
                                creditCardFeeTotal,
                                processStep,
                                timeSinceStart: paymentStartTime.current ? Date.now() - paymentStartTime.current : null,
                                sessionId: sessionId.current
                            });
                            setLoading(true);
                            break;
                        case 'eCheckProcessed':
                        case 'processed':
                            logPaymentEvent('NEXIO_PAYMENT_PROCESSED', {
                                eventType: event.data.event,
                                paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                                transactionStatus: event.data.data.transactionStatus,
                                refNumber: event.data.data.gatewayResponse && event.data.data.gatewayResponse.refNumber,
                                processingTime: paymentStartTime.current ? Date.now() - paymentStartTime.current : null
                            });
                            savePaymentData(event.data.data);
                            break;
                        case 'cardSaved':
                            // Clear card tokenization timeout since we received the cardSaved event
                            if (cardTokenizationTimeoutId) {
                                clearTimeout(cardTokenizationTimeoutId);
                                setCardTokenizationTimeoutId(null);
                            }

                            const {customer} = event.data.data.data
                            logPaymentEvent('NEXIO_CARD_SAVED', {
                                billToState: customer.billToState,
                                billToCountry: customer.billToCountry,
                                lastFour: event.data.data.token && event.data.data.token.lastFour,
                                tokenCreated: !!(event.data.data.token && event.data.data.token.token),
                                timeToCardSaved: paymentStartTime.current ? Date.now() - paymentStartTime.current : null
                            });
                            // check surcharge
                            if (customer.billToCountry === 'US') {
                                logPaymentEvent('SURCHARGE_VALIDATION_START', {
                                    billToState: customer.billToState,
                                    billToCountry: customer.billToCountry,
                                    hasToken: !!(event.data.data.token && event.data.data.token.token),
                                    timeSinceCardSaved: 0,
                                    sessionId: sessionId.current
                                });

                                // Set progressive timeout for surcharge validation API call
                                const timeouts = getProgressiveTimeouts();
                                const SURCHARGE_VALIDATION_TIMEOUT_MS = timeouts.surchargeValidation;
                                const surchargeTimeoutId = setTimeout(() => {
                                    logDiagnosticInfo('SURCHARGE_VALIDATION_TIMEOUT', {
                                        timeoutDuration: SURCHARGE_VALIDATION_TIMEOUT_MS,
                                        billToState: customer.billToState,
                                        hasToken: !!(event.data.data.token && event.data.data.token.token),
                                        specificContext: 'Waiting for sendValidateSurcharge API response'
                                    });

                                    setLoading(false);
                                    setError(getEscalatedErrorMessage('surchargeValidation'));
                                    const isRetryable = retryAttempts < 3;
                                    setIsRecoverableError(isRetryable); // Only allow retry for first 3 attempts

                                    if (!isRetryable) {
                                        logPaymentEvent('MAX_RETRIES_REACHED', {
                                            retryAttempts,
                                            maxRetries: 3,
                                            timeoutType: 'surchargeValidation',
                                            sessionId: sessionId.current
                                        }, WcvLogLevel.ERROR);
                                    }

                                    enqueueSnackbar(getEscalatedSnackbarMessage(SURCHARGE_VALIDATION_TIMEOUT_MS, 'Surcharge validation'), {variant: 'error'});
                                }, SURCHARGE_VALIDATION_TIMEOUT_MS);

                                sendValidateSurcharge(
                                    {
                                        variables: {
                                            data: {
                                                billingState: customer.billToState,
                                                token: event.data.data.token.token,
                                            }
                                        }
                                    }
                                ).then(resp => {
                                    // Clear timeout on successful response
                                    clearTimeout(surchargeTimeoutId);
                                    setStateAbbr(customer.billToState)
                                    const data = resp && resp.data && resp.data.validateSurcharge && resp.data.validateSurcharge.data;
                                    if (data) {
                                        const parsedData = JSON.parse(data);
                                        logPaymentEvent('SURCHARGE_VALIDATION_COMPLETE', {
                                            billToState: customer.billToState,
                                            surchargeAllowed: parsedData.surchargeAllowed,
                                            validationResponse: parsedData
                                        });
                                        setProcessStep(2)
                                        setLoading(false)
                                        setSurchargeAllowed(parsedData.surchargeAllowed)
                                    }
                                }).catch(validationError => {
                                    // Clear timeout on error response
                                    clearTimeout(surchargeTimeoutId);
                                    logPaymentEvent('SURCHARGE_VALIDATION_ERROR', {
                                        billToState: customer.billToState,
                                        error: validationError.message,
                                        totalTime: paymentStartTime.current ? Date.now() - paymentStartTime.current : null
                                    }, WcvLogLevel.ERROR);
                                    setLoading(false);
                                    setError('Payment validation failed. Please try again or contact support at 1-800-222-4432.');
                                    setIsRecoverableError(true);
                                })
                            } else {
                                setProcessStep(2)
                                setLoading(false)
                            }
                            setCardToken(event.data.data.token.token)
                            break;
                        case 'error':
                            logPaymentEvent('NEXIO_ERROR', {
                                errorMessage: event.data.data.message,
                                errorCode: event.data.data.code,
                                fullErrorData: event.data.data,
                                timeToError: paymentStartTime.current ? Date.now() - paymentStartTime.current : null
                            }, WcvLogLevel.ERROR);
                            setLoading(false);
                            // console.log(event.data);
                            setErrorFromNexio(event.data.data.message);
                            storePaymentErrorLog(event.data.data);
                            // Don't reset state on Nexio errors - user should be able to retry
                            break;
                    }
                }
            }
        };
        window.addEventListener('message', messageListener);
        return () => {
            window.removeEventListener('message', messageListener);
            if (iframeLoadTimeout) {
                clearTimeout(iframeLoadTimeout);
            }
        };
    });

    const setErrorFromNexio = (errorMessage: string) => {
        let newMessage = 'Payment Error: Please contact us at 1-800-222-4432 for more information.';
        const contactBankMessage = 'Please contact your bank, if that does not resolve the issue then please contact WCV at 1-800-222-4432 for more information.';
        if (errorMessage.indexOf('Credit card has expired') === 0) {
            newMessage = 'Credit card has expired: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Verify CVC Failed') === 0) {
            newMessage = 'Verify CVC Failed: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Card number error') === 0) {
            newMessage = 'Card number error: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('VCERR') === 0) {
            newMessage = 'Payment Error: Routing number invalid.';
        }
        if (errorMessage.indexOf('Declined') === 0) {
            newMessage = 'Declined: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Pickup Card') === 0) {
            newMessage = 'Pickup Card: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Do not Honor') === 0) {
            newMessage = 'Do not Honor: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Invalid Transaction') === 0) {
            newMessage = 'Invalid Transaction: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Invalid Issuer') === 0) {
            newMessage = 'Invalid Issuer: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Unable to locate Record') === 0) {
            newMessage = 'Unable to locate Record: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Insufficient funds') === 0) {
            newMessage = 'Insufficient funds: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Invalid Pin') === 0) {
            newMessage = 'Invalid Pin: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Transaction Not Permitted') === 0) {
            newMessage = 'Transaction Not Permitted: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Restricted Card') === 0) {
            newMessage = 'Restricted Card: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Excess withdrawal count') === 0) {
            newMessage = 'Excess withdrawal count: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Allowable number of pin tries exceeded') === 0) {
            newMessage = 'Allowable number of pin tries exceeded: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('No checking account') === 0) {
            newMessage = 'No checking account: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Declined for CVV failure') === 0) {
            newMessage = 'Declined for CVV failure: ' + contactBankMessage;
        }
        if (errorMessage.indexOf('Withdrawal Limit Exceeded') === 0) {
            newMessage = 'Withdrawal Limit Exceeded: ' + contactBankMessage;
        }
        setError(newMessage);
    };

    // Log when new token is fetched
    useEffect(() => {
        if (nexioToken && nexioToken.token) {
            console.log('NEW TOKEN FETCHED - Token:', nexioToken.token.substring(0, 12) + '...', 'PaymentType:', paymentType === PaymentType.CARD ? 'CARD' : paymentType === PaymentType.ECHEQUE ? 'ECHEQUE' : 'NONE');
        }
    }, [nexioToken]);

    // Track payment type changes
    useEffect(() => {
        console.log('PAYMENT TYPE CHANGED:', paymentType === PaymentType.CARD ? 'CARD' : paymentType === PaymentType.ECHEQUE ? 'ECHEQUE' : 'NONE/INITIAL');
    }, [paymentType]);

    // Track frameUrl changes
    useEffect(() => {
        console.log('FRAME URL CHANGED:', frameUrl ? frameUrl.substring(0, 60) + '...' : 'CLEARED');
    }, [frameUrl]);

    // Track errors
    useEffect(() => {
        if (error) {
            console.error('PAYMENT ERROR:', error);
        }
    }, [error]);

    useEffect(() => {
        setError(undefined);

        if (paymentType !== -1 && nexioToken && nexioToken.baseUrl && nexioToken.token && processStep === 1) {
            // Store token in local variable for type safety
            const token = nexioToken.token;

            console.log('IFRAME SETUP - Token:', token.substring(0, 12) + '...', 'PaymentType:', paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE', 'Already used?', usedTokens.has(token));

            // Check if this token has already been used
            if (usedTokens.has(token)) {
                logPaymentEvent('NEXIO_TOKEN_ALREADY_USED', {
                    paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                    token: token.substring(0, 8) + '...',
                    usedTokensCount: usedTokens.size,
                    processStep
                }, WcvLogLevel.ERROR);

                setError('This payment form has expired. Please close and reopen the payment window to try again.');
                setIsRecoverableError(true);
                setLoading(false);
                return;
            }

            // Mark token as used IMMEDIATELY before creating iframe
            setUsedTokens(prev => {
                const newSet = new Set(prev).add(token);
                console.log('MARKED TOKEN AS USED (iframe setup) - Token:', token.substring(0, 12) + '...', 'Total used tokens:', newSet.size);
                return newSet;
            });

            setLoading(true);
            let nexioUrl = nexioToken.baseUrl;
            if (paymentType === PaymentType.CARD) nexioUrl += '/pay/v3/saveCard';
            if (paymentType === PaymentType.ECHEQUE) nexioUrl += '/pay/v3/processECheck';
            nexioUrl += '?token=' + token + '&shouldReturnHtml=true';
            setFrameUrl(nexioUrl);

            logPaymentEvent('NEXIO_IFRAME_SETUP', {
                paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                baseUrl: nexioToken.baseUrl,
                hasToken: true,
                fullUrl: nexioUrl,
                tokenMarkedAsUsed: true
            });

            // Set timeout for iframe loading - soft warning, not hard error
            const IFRAME_SOFT_TIMEOUT_MS = 45000; // 45 second timeout
            const timeout = setTimeout(() => {
                logPaymentEvent('NEXIO_IFRAME_SLOW_LOAD', {
                    paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                    frameUrl: nexioUrl,
                    baseUrl: nexioToken.baseUrl,
                    token: nexioToken.token ? nexioToken.token.substring(0, 8) + '...' : 'missing',
                    timeoutDuration: IFRAME_SOFT_TIMEOUT_MS,
                    timeSinceStart: paymentStartTime.current ? Date.now() - paymentStartTime.current : null,
                    expectedPath: paymentType === PaymentType.CARD ? '/pay/v3/saveCard' : '/pay/v3/processECheck',
                    fullTokenData: nexioToken
                }, WcvLogLevel.WARN);

                // Show gentle notification but keep loading - don't set error state
                enqueueSnackbar('Payment form is taking longer than usual to load. Please wait...', {
                    variant: 'info',
                    autoHideDuration: 8000
                });

                // Keep loading state active - don't stop the process
                setLoading(true);

                // Keep listening for 'loaded' event - timeout will be cleared if it arrives
            }, IFRAME_SOFT_TIMEOUT_MS);

            setIframeLoadTimeout(timeout);
        }

        if (nexioToken && nexioToken.error) {
            logPaymentEvent('NEXIO_TOKEN_GENERATION_ERROR', {
                error: nexioToken.error,
                paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                amount: nexioAmount,
                billingPartyMemberId,
                tokenData: nexioToken
            }, WcvLogLevel.ERROR);
            setError(nexioToken.error);
        }
    }, [nexioToken, paymentType]);

    // Invalidate token when payment type changes to force fresh token fetch
    useEffect(() => {
        // Skip on initial render (when paymentType is -1)
        if (paymentType === -1) {
            return;
        }

        // If we have a nexioToken and it's not already used, mark it as used
        // This forces a fresh token fetch when user switches payment types
        if (nexioToken && nexioToken.token && !usedTokens.has(nexioToken.token)) {
            // Store token in local variable for type safety
            const token = nexioToken.token;

            console.log('PAYMENT TYPE CHANGE - Invalidating token:', token.substring(0, 12) + '...', 'New PaymentType:', paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE');

            logPaymentEvent('NEXIO_TOKEN_INVALIDATED_ON_TYPE_CHANGE', {
                oldPaymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                token: token.substring(0, 8) + '...',
                reason: 'Payment type changed'
            }, WcvLogLevel.INFO);

            // Mark current token as used to prevent reuse
            setUsedTokens(prev => {
                const newSet = new Set(prev).add(token);
                console.log('MARKED TOKEN AS USED (type change) - Token:', token.substring(0, 12) + '...', 'Total used tokens:', newSet.size);
                return newSet;
            });

            // Clear frameUrl to force iframe recreation with new token
            setFrameUrl('');
        }
    }, [paymentType]); // Only trigger on paymentType change

    const resetPaymentState = () => {
        setSurchargeAllowed(false);
        setStateAbbr('');
        setCreditCardFee(0);
        setCreditCardFeeTotal(0);
        setNexioAmount(amount); // Reset to original amount
        setProcessStep(1);
        setError(undefined);
        setIsRecoverableError(false);
        setRetryAttempts(0);
        setPaymentProcessingAttempted(false);
        setUsedTokens(new Set());
        setCardToken(undefined);
        setPaymentType(-1);
        setisNexioValid(false);
        setMemberHasAutomaticPayment(false);
        setPaymentFrequency(-1);
        setPaymentFrequencyError(false);
        setFrameUrl('');
        setLoading(false);
        setProcessing(false);

        // Clear payment timeout if active
        if (paymentTimeoutId) {
            clearTimeout(paymentTimeoutId);
            setPaymentTimeoutId(null);
        }

        // Clear card tokenization timeout if active
        if (cardTokenizationTimeoutId) {
            clearTimeout(cardTokenizationTimeoutId);
            setCardTokenizationTimeoutId(null);
        }

        if (tokenRequestTimeoutRef.current) {
            clearTimeout(tokenRequestTimeoutRef.current);
            tokenRequestTimeoutRef.current = null;
        }
    };

    const handleCancel = () => {
        if (iframeLoadTimeout) {
            clearTimeout(iframeLoadTimeout);
            setIframeLoadTimeout(null);
        }
        resetPaymentState();
        onClose();
    }

    // Get progressive timeout durations based on retry attempts
    const getProgressiveTimeouts = () => {
        const isDev = process.env.REACT_APP_HOST_ENV === 'local';

        switch (retryAttempts) {
            case 0: // First attempt
                return {
                    cardTokenization: isDev ? 10000 : 30000,
                    surchargeValidation: isDev ? 35000 : 35000,  // Backend has 30s timeout + 5s buffer
                    paymentProcessing: isDev ? 35000 : 35000     // Backend has 30s timeout + 5s buffer (data shows 2-4s typical)
                };
            case 1: // Second attempt - slightly longer
                return {
                    cardTokenization: isDev ? 15000 : 45000,
                    surchargeValidation: isDev ? 40000 : 40000,
                    paymentProcessing: isDev ? 40000 : 40000
                };
            case 2: // Third attempt - much longer
                return {
                    cardTokenization: isDev ? 20000 : 60000,
                    surchargeValidation: isDev ? 45000 : 45000,
                    paymentProcessing: isDev ? 45000 : 45000
                };
            default: // 4+ attempts - maximum timeouts
                return {
                    cardTokenization: isDev ? 25000 : 75000,
                    surchargeValidation: isDev ? 50000 : 50000,
                    paymentProcessing: isDev ? 50000 : 50000
                };
        }
    };

    // Get escalated error messages based on retry attempts
    const getEscalatedErrorMessage = (timeoutType: 'tokenRequest' | 'cardTokenization' | 'surchargeValidation' | 'paymentProcessing') => {
        const baseMessages = {
            tokenRequest: 'Payment form is taking longer than expected to start.',
            cardTokenization: 'Card validation is taking longer than expected.',
            surchargeValidation: 'Payment validation is taking longer than expected.',
            paymentProcessing: 'Payment is taking longer than expected.'
        };

        const baseMessage = baseMessages[timeoutType] || 'Payment is taking longer than expected.';
        const contactInfo = 'Please try again or contact support at 1-800-222-4432.';

        switch (retryAttempts) {
            case 0:
                return `${baseMessage} ${contactInfo}`;
            case 1:
                return `${baseMessage} This may take longer on slow connections. ${contactInfo}`;
            case 2:
                return `${baseMessage} Payment system may be experiencing issues. ${contactInfo}`;
            default:
                return `Unable to process payment after multiple attempts. Please try again later or contact support at 1-800-222-4432 for immediate assistance.`;
        }
    };

    // Get escalated snackbar messages
    const getEscalatedSnackbarMessage = (timeoutMs: number, timeoutType: string) => {
        const seconds = timeoutMs / 1000;
        switch (retryAttempts) {
            case 0:
                return `${timeoutType} timed out after ${seconds} seconds. Please try again.`;
            case 1:
                return `Still having trouble after ${seconds} seconds. Trying with longer timeout...`;
            case 2:
                return `Payment system may be slow. Extended timeout of ${seconds} seconds reached.`;
            default:
                return `Multiple timeout failures detected. Please contact support.`;
        }
    };

    // Comprehensive diagnostic logging for spinning/timeout issues
    const logDiagnosticInfo = (eventType: string, additionalInfo: any = {}) => {
        const diagnosticData = {
            // Current state
            currentState: {
                processStep,
                loading,
                processing,
                isNexioValid,
                error: !!error,
                isRecoverableError,
                retryAttempts,
                paymentType: paymentType === PaymentType.CARD ? 'CARD' : paymentType === PaymentType.ECHEQUE ? 'ECHEQUE' : 'UNKNOWN',
                hasCardToken: !!cardToken,
                frameUrl: !!frameUrl,
                nexioAmount,
                amount
            },
            // Timeouts status
            activeTimeouts: {
                iframeLoadTimeout: !!iframeLoadTimeout,
                paymentTimeoutId: !!paymentTimeoutId,
                cardTokenizationTimeoutId: !!cardTokenizationTimeoutId
            },
            // Session info
            sessionInfo: {
                sessionId: sessionId.current,
                totalTime: paymentStartTime.current ? Date.now() - paymentStartTime.current : null,
                billingPartyId,
                billingPartyMemberId
            },
            // Browser/network info
            browserInfo: {
                userAgent: navigator.userAgent,
                onLine: navigator.onLine,
                connectionType: (navigator as any).connection ? (navigator as any).connection.effectiveType : 'unknown',
                cookieEnabled: navigator.cookieEnabled
            },
            // Additional context
            ...additionalInfo
        };

        logPaymentEvent(`DIAGNOSTIC_${eventType}`, diagnosticData, WcvLogLevel.ERROR);
    };

    // Development-only function to manually trigger timeout for testing
    const triggerManualTimeout = () => {
        if (process.env.REACT_APP_HOST_ENV === 'local') {
            logPaymentEvent('MANUAL_TIMEOUT_TRIGGERED', {
                processStep,
                nexioAmount,
                cardToken: !!cardToken,
                paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                sessionId: sessionId.current,
                userAgent: navigator.userAgent
            }, WcvLogLevel.INFO);

            setLoading(false);
            setError('Payment timeout manually triggered for testing. Please try again or contact support at 1-800-222-4432.');
            setIsRecoverableError(true);
            enqueueSnackbar('Manual timeout triggered for testing. Try Again button should appear.', {variant: 'warning'});
        }
    };
    const onPaymentSubmit = () => {
        logPaymentEvent('SUBMIT_BUTTON_CLICKED', {
            processStep,
            nexioAmount,
            cardToken: !!cardToken,
            isNexioValid,
            memberHasAutomaticPayment,
            paymentFrequency
        });

        setLoading(true)
        if (processStep === 2) {
            // CRITICAL: Check for duplicate payment prevention
            if (cardToken && usedTokens.has(cardToken)) {
                logPaymentEvent('DUPLICATE_PAYMENT_PREVENTED', {
                    token: cardToken.substring(0, 8) + '...',
                    retryAttempts,
                    usedTokensCount: usedTokens.size,
                    sessionId: sessionId.current
                }, WcvLogLevel.ERROR);

                setLoading(false);
                setError('This payment has already been attempted. Please refresh the page to start a new payment or contact support at 1-800-222-4432.');
                setIsRecoverableError(false);
                enqueueSnackbar('Duplicate payment prevented for your protection.', {variant: 'warning'});
                return;
            }

            // Mark token as used and set payment processing flag
            if (cardToken) {
                setUsedTokens(prev => new Set(prev).add(cardToken));
                setPaymentProcessingAttempted(true);
            }

            const apiStartTime = Date.now();
            logPaymentEvent('PROCESS_PAYMENT_API_START', {
                token: !!cardToken,
                amount: nexioAmount,
                paymentType: memberHasAutomaticPayment ?
                    'INIT_SCHEDULED' :
                    isFirstPayment ?
                        'INIT_UNSCHEDULED' :
                        'UNSCHEDULED_CIT',
                isRetry: retryAttempts > 0,
                paymentProcessingAttempted
            });

            // Set progressive timeout for payment processing
            const timeouts = getProgressiveTimeouts();
            const PAYMENT_TIMEOUT_MS = timeouts.paymentProcessing;
            const timeoutId = setTimeout(() => {
                logDiagnosticInfo('PAYMENT_PROCESSING_TIMEOUT', {
                    timeoutDuration: PAYMENT_TIMEOUT_MS,
                    apiStartTime,
                    specificContext: 'Waiting for sendProcessPayment API response - CRITICAL: No retry allowed to prevent duplicate payments'
                });

                setLoading(false);

                // CRITICAL: Never allow retries for payment processing timeouts to prevent duplicate charges
                setError('Payment processing timed out. This may indicate a network issue or the payment may still be processing. Please wait a few minutes and check your account before attempting another payment, or contact support at 1-800-222-4432.');
                setIsRecoverableError(false); // Never allow retry for payment processing to prevent duplicates

                logPaymentEvent('PAYMENT_PROCESSING_TIMEOUT_NO_RETRY', {
                    retryAttempts,
                    timeoutType: 'paymentProcessing',
                    sessionId: sessionId.current,
                    duplicatePreventionActive: true
                }, WcvLogLevel.ERROR);

                enqueueSnackbar('Payment processing timed out. Check your account before trying again to avoid duplicate charges.', {variant: 'error'});
            }, PAYMENT_TIMEOUT_MS);

            setPaymentTimeoutId(timeoutId);

            sendProcessPayment(
                {
                    variables: {
                        data: {
                            token: cardToken,
                            amount: nexioAmount,
                            /*
                            * first automatic payment should be INIT_SCHEDULED
                            * first payment not automatic should be INIT_UNSCHEDULED
                            * non-first payment should be UNSCHEDULED_CIT (cust initiated)
                            * */
                            paymentType: memberHasAutomaticPayment ?
                                NexioPaymentTypes.INIT_SCHEDULED :
                                isFirstPayment ?
                                    NexioPaymentTypes.INIT_UNSCHEDULED :
                                    NexioPaymentTypes.UNSCHEDULED_CIT,
                        }
                    }
                }
            ).then((resp) => {
                // Clear timeout on success
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    setPaymentTimeoutId(null);
                }

                const apiResponseTime = Date.now() - apiStartTime;
                logPaymentEvent('PROCESS_PAYMENT_API_RESPONSE', {
                    responseTime: apiResponseTime,
                    status: resp && resp.data && resp.data.processPayment && resp.data.processPayment.status,
                    hasData: !!(resp && resp.data && resp.data.processPayment && resp.data.processPayment.data),
                    hasError: !!(resp && resp.data && resp.data.processPayment && resp.data.processPayment.error)
                });

                // @ts-ignore
                const status = resp.data.processPayment.status;

                if (status === 'SUCCESS') {
                    // Parse the data
                    const data = resp && resp.data && resp.data.processPayment && resp.data.processPayment.data;
                    if (data) {
                        const parsedData = JSON.parse(data);

                        // Check if this is actually a decline (SUCCESS status but payment declined)
                        const isDecline = parsedData.error ||
                            (parsedData.gatewayResponse && parsedData.gatewayResponse.status === 'declined');

                        if (isDecline) {
                            // Handle as decline
                            setLoading(false);
                            logPaymentEvent('PROCESS_PAYMENT_DECLINED', {
                                status,
                                declineReason: parsedData.message || parsedData.error,
                                responseTime: apiResponseTime
                            }, WcvLogLevel.WARN);
                            // @ts-ignore
                            setErrorFromNexio(parsedData.message || (parsedData.error && (typeof parsedData.error === 'string' ? parsedData.error : JSON.stringify(parsedData.error))));
                            // Don't reset state on declines - user should be able to retry
                            throw Error('Payment Declined');
                        } else {
                            // Real success - save payment
                            savePaymentData(parsedData);
                        }
                    }
                } else {
                    // TRUE API error (not a decline)
                    setLoading(false);
                    // @ts-ignore
                    const paymentError = JSON.parse(resp.data.processPayment.error);
                    logPaymentEvent('PROCESS_PAYMENT_API_ERROR', {
                        status,
                        error: paymentError,
                        responseTime: apiResponseTime
                    }, WcvLogLevel.ERROR);
                    // @ts-ignore
                    setErrorFromNexio(paymentError.message);
                    // Don't reset state on payment processing errors - user should be able to retry
                    throw Error('Failed');
                }
                logPaymentEvent('PAYMENT_FLOW_COMPLETED', {
                    totalTime: paymentStartTime.current ? Date.now() - paymentStartTime.current : null,
                    apiResponseTime
                });
                onClose()
                enqueueSnackbar('Payment Process', {
                    variant: 'success'
                })
            }).catch((catchError) => {
                // Clear timeout on error
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    setPaymentTimeoutId(null);
                }

                const apiResponseTime = Date.now() - apiStartTime;
                logPaymentEvent('PROCESS_PAYMENT_API_CATCH', {
                    error: catchError.message,
                    responseTime: apiResponseTime,
                    totalTime: paymentStartTime.current ? Date.now() - paymentStartTime.current : null
                }, WcvLogLevel.ERROR);
                enqueueSnackbar('Payment Failed!', {variant: 'error'});
                // Don't reset state on payment processing errors - user should be able to retry
            }).finally(() => {
                setLoading(false)
            });
        }
        if (memberHasAutomaticPayment) {
            if (paymentFrequency === -1) {
                setPaymentFrequencyError(true);
                return;
            }
        }

        if (frameRef && frameRef.current && frameRef.current.contentWindow) {
            if (nexioToken && nexioToken.baseUrl) {
                logPaymentEvent('NEXIO_CARD_TOKENIZATION_START', {
                    processStep,
                    paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                    nexioAmount,
                    sessionId: sessionId.current
                });

                frameRef.current.contentWindow.postMessage('posted', nexioToken.baseUrl);

                // Set progressive timeout for card tokenization process (waiting for cardSaved event)
                const timeouts = getProgressiveTimeouts();
                const CARD_TOKENIZATION_TIMEOUT_MS = timeouts.cardTokenization;
                const tokenizationTimeoutId = setTimeout(() => {
                    logDiagnosticInfo('CARD_TOKENIZATION_TIMEOUT', {
                        timeoutDuration: CARD_TOKENIZATION_TIMEOUT_MS,
                        specificContext: 'Waiting for Nexio cardSaved event after postMessage'
                    });

                    setLoading(false);
                    setError(getEscalatedErrorMessage('cardTokenization'));
                    const isRetryable = retryAttempts < 3;
                    setIsRecoverableError(isRetryable); // Only allow retry for first 3 attempts

                    if (!isRetryable) {
                        logPaymentEvent('MAX_RETRIES_REACHED', {
                            retryAttempts,
                            maxRetries: 3,
                            timeoutType: 'cardTokenization',
                            sessionId: sessionId.current
                        }, WcvLogLevel.ERROR);
                    }

                    enqueueSnackbar(getEscalatedSnackbarMessage(CARD_TOKENIZATION_TIMEOUT_MS, 'Card tokenization'), {variant: 'error'});
                }, CARD_TOKENIZATION_TIMEOUT_MS);

                setCardTokenizationTimeoutId(tokenizationTimeoutId);
            }
        }
    };

    const handleAddCreditCardAdjustment = (paymentId: number | null) => {
        const sanitisedData: AddCreditCardAdjustmentInput = {
            id: billingParty!.id,
            amount: Math.round((creditCardFeeTotal || 0) * 100) / 100,
            payment_id: paymentId || null
        };
        sendAddCreditCardAdjustment({variables: sanitisedData})
    };

    let cardHeight = 250;
    if (paymentType === -1) {
        cardHeight = 300;
        if (message) cardHeight += 50;
    }
    if (autoPaymentEligible) cardHeight = 350;
    if (paymentType === PaymentType.CARD || paymentType === PaymentType.ECHEQUE) cardHeight = 700;
    if (error || processStep === 2) cardHeight = 250;

    // Log when modal opens/closes
    useEffect(() => {
        if (open && !paymentStartTime.current) {
            paymentStartTime.current = Date.now();
            logPaymentEvent('MODAL_OPENED', {
                amount: props.amount,
                billingPartyId,
                billingPartyMemberId: props.billingPartyMemberId,
                isFirstPayment,
                message
            });
        }
    }, [open]);

    if (!open) return null;

    return (
        <Modal onClose={onClose} open={open}>
            <Card className={classes.root} style={{height: cardHeight}}>
                <LoadingOverlay loading={loadingData.loading || loading}>
                    {!processing && (
                        <React.Fragment>
                            <CardHeader title={
                                'Make Payment: ' +
                                (paymentType === 0 ? PaymentTypes[PaymentType.CARD].name.charAt(0).toUpperCase() + PaymentTypes[PaymentType.CARD].name.substr(1) : '') +
                                (paymentType === 1 ? PaymentTypes[PaymentType.ECHEQUE].name.charAt(0).toUpperCase() + PaymentTypes[PaymentType.ECHEQUE].name.substr(1) : '')
                            }/>

                            <CardContent className={classes.container}>
                                {(paymentType === -1) && (
                                    <Grid container spacing={3}>

                                        {message && (
                                            <Grid item xs={12}>
                                                <Typography variant={'body1'}>{message}</Typography>
                                            </Grid>
                                        )}

                                        <Grid item xs={12}>
                                            {payingMembers && (
                                                <TextField
                                                    fullWidth
                                                    label="Billing Party Member"
                                                    name="billing_party_member_id"
                                                    onChange={(event) => setBillingPartyMemberId(Number(event.target.value))}
                                                    select
                                                    // eslint-disable-next-line react/jsx-sort-props
                                                    SelectProps={{native: true}}
                                                    value={billingPartyMemberId}
                                                    variant="outlined"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                    disabled={!!props.billingPartyMemberId}
                                                >
                                                    <option value={-1}>Please select...</option>
                                                    {payingMembers.map(member => (
                                                        <option key={member.id} value={member.id}>
                                                            {member.first_name} {member.last_name} ({member.email})
                                                        </option>
                                                    ))}
                                                </TextField>
                                            )}
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                type={'number'}
                                                label="amount"
                                                name="amount"
                                                variant="outlined"
                                                onChange={(event) => setAmount(Number(event.target.value))}
                                                onFocus={event => event.target.select()}
                                                value={amount}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                    inputProps: {
                                                        min: 0,
                                                        onClick: (e) => e.currentTarget.select(),
                                                        onFocus: (event) => {
                                                            event.preventDefault();
                                                            const {target} = event;

                                                            if (target) {
                                                                //@ts-ignore
                                                                // noinspection JSConstantReassignment
                                                                target.type = 'text';
                                                                target.setSelectionRange(0, target.value.length);
                                                                //@ts-ignore
                                                                // noinspection JSConstantReassignment
                                                                target.type = 'number';

                                                            }
                                                        },
                                                    }
                                                }}
                                                disabled={!!props.amount}
                                            />
                                        </Grid>
                                    </Grid>
                                )}

                                {paymentType !== -1 && (
                                    <Grid container spacing={0} className={classes.summaryTable}>
                                        <Grid item xs={9}><Typography variant={'body1'}>Item</Typography></Grid>
                                        <Grid item xs={3}>
                                            <Typography variant={'body1'} style={{textAlign: 'right'}}>Amount</Typography>
                                        </Grid>
                                        {processStep === 2 && surchargeAllowed && (
                                            <React.Fragment>
                                                <Grid item xs={9}>
                                                    <Typography variant={'body1'}>
                                                        Credit Card Fee ({creditCardFee}%)
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Typography variant={'body1'} style={{textAlign: 'right'}}>
                                                        {creditCardFeeTotal !== undefined ? formatMoney(creditCardFeeTotal, true) : '$0'}
                                                    </Typography>
                                                </Grid>
                                            </React.Fragment>
                                        )}

                                        <Grid item xs={9}><Typography
                                            variant={'body1'}>Payment Due</Typography></Grid>
                                        <Grid item xs={3}>
                                            <Typography variant={'body1'} style={{textAlign: 'right'}}>
                                                {formatMoney(amount, true)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={9}><Typography variant={'body1'} style={{
                                            textAlign: 'right',
                                            fontWeight: 'bold'
                                        }}>Total</Typography></Grid>
                                        <Grid item xs={3}><Typography variant={'body1'} style={{
                                            textAlign: 'right',
                                            fontWeight: 'bold'
                                        }}>{formatMoney(nexioAmount, true)}</Typography></Grid>
                                    </Grid>
                                )}

                                {paymentType === PaymentType.ECHEQUE && (
                                    <Grid container spacing={0}>
                                        <Grid item xs={12}>
                                            <Typography variant={'body1'} color={'error'}>
                                                E-check payments take 5 - 7 business days to clear and show as “complete” on portal. Until “complete”
                                                status it will remain in past due status and not be included in the Received total. You can find the
                                                payment under “My Billing Parties” on the left side panel.
                                                Please refer to the question bubble to see a diagram that shows where to find your Routing Number and
                                                Account Number
                                                <InfoPopover>
                                                    <img alt="check image" className={classes.image} src={image}/>
                                                </InfoPopover>
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                )
                                }

                                {error && (
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <Typography variant={'body1'} color={'error'}>{error}</Typography>
                                        </Grid>
                                    </Grid>
                                )}

                                {!error && paymentType !== -1 && processStep === 1 && (
                                    <iframe
                                        ref={frameRef}
                                        src={frameUrl}
                                        className={classes.iframe}
                                        onError={() => {
                                            logPaymentEvent('NEXIO_IFRAME_ON_ERROR', {
                                                paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                                                frameUrl,
                                                userAgent: navigator.userAgent,
                                                timeToError: paymentStartTime.current ? Date.now() - paymentStartTime.current : null
                                            }, WcvLogLevel.ERROR);
                                            setLoading(false);
                                            if (iframeLoadTimeout) {
                                                clearTimeout(iframeLoadTimeout);
                                                setIframeLoadTimeout(null);
                                            }
                                            setError('Payment form failed to load due to security restrictions. Please try refreshing the page or contact support at 1-800-222-4432.');
                                            setIsRecoverableError(true);
                                        }}
                                        onLoad={() => {
                                            logPaymentEvent('NEXIO_IFRAME_ON_LOAD', {
                                                paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                                                frameUrl,
                                                userAgent: navigator.userAgent,
                                                loading,
                                                isNexioValid,
                                                note: 'onLoad fired - no fallback timeout set to prevent mobile false positives'
                                            }, WcvLogLevel.INFO);
                                        }}
                                    />
                                )}

                                {processStep === 1 &&
                                    <>
                                        <Grid container spacing={3} style={{marginTop: 15}}>
                                            <Grid item xs={12}>
                                                <Button onClick={() => {
                                                    logPaymentEvent('PAYMENT_TYPE_SELECTED', {
                                                        paymentType: 'CARD',
                                                        amount,
                                                        billingPartyMemberId
                                                    });
                                                    setPaymentType(PaymentType.CARD);
                                                    setNexioAmount(amount);
                                                }} variant="contained"
                                                        disabled={amount <= 0 || billingPartyMemberId === -1}>
                                                    {PaymentTypes[PaymentType.CARD].name}
                                                </Button>

                                                <Button onClick={() => {
                                                    logPaymentEvent('PAYMENT_TYPE_SELECTED', {
                                                        paymentType: 'ECHEQUE',
                                                        amount,
                                                        billingPartyMemberId
                                                    });
                                                    setPaymentType(PaymentType.ECHEQUE);
                                                    setNexioAmount(amount);
                                                }} variant="contained" disabled={amount <= 0 || billingPartyMemberId === -1}
                                                        style={{marginLeft: 10}}>
                                                    {PaymentTypes[PaymentType.ECHEQUE].name}
                                                </Button>

                                                {isFirstPayment && (
                                                    <Button onClick={onClose} variant="contained" style={{marginLeft: 10}}>
                                                        <T phrase="Paper Check"/>
                                                    </Button>
                                                )}
                                            </Grid>
                                        </Grid>
                                        {(autoPaymentEligible) && (
                                            <React.Fragment>
                                                {(autoPaymentEligible) && (
                                                    <Grid item xs={12}>
                                                        <Grid component="label" container alignItems="center" spacing={1}>
                                                            <Grid item>
                                                                <Switch
                                                                    checked={memberHasAutomaticPayment}
                                                                    color="secondary"
                                                                    edge="start"
                                                                    name="is_automatic_payments"
                                                                    onChange={(e) => {
                                                                        setMemberHasAutomaticPayment(e.target.checked);
                                                                    }}
                                                                    value={false}
                                                                />
                                                            </Grid>
                                                            <Grid item>
                                                                <Typography variant="h5">
                                                                    Manage Automatic Payments
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                )}
                                                {memberHasAutomaticPayment && (
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                select
                                                                fullWidth
                                                                label="Select Payment Frequency"
                                                                value={paymentFrequency}
                                                                onChange={event => {
                                                                    setPaymentFrequency(
                                                                        parseInt(event.target.value, 10)
                                                                    )
                                                                    setPaymentFrequencyError(false)
                                                                }}
                                                                variant="outlined"
                                                                margin="dense"
                                                                SelectProps={{
                                                                    native: false
                                                                }}
                                                            >
                                                                <MenuItem value={0}>
                                                                    <T phrase="Pay according to Payment Schedule"/>
                                                                </MenuItem>
                                                            </TextField>
                                                            {paymentFrequencyError && (
                                                                <Typography variant="body1" color="error">
                                                                    <T phrase="Please select a payment frequency"/>
                                                                </Typography>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                )}
                                            </React.Fragment>
                                        )}
                                    </>
                                }
                            </CardContent>
                            {paymentType !== -1 && processStep === 1 &&
                                <CardContent>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Typography variant={'body1'} className={classes.processingFeeMessage}>
                                                Starting on October 7, 2025, all credit card payments may incur a surcharge fee (where allowed by country and state laws). Paying with a debit card will not incur a surcharge fee. Please proceed to the next page to review the final cost.
                                                <br/>
                                                <br/>
                                                Click cancel if you wish to make an e-check payment with no surcharge. E-check payments take 5 to 7 business days to clear and show as “complete” on your account.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            }
                            <CardActions className={classes.actions} style={{bottom: processStep === 2 ? 0 : 'unset'}}>
                                <Button onClick={handleCancel} variant="contained" className={classes.deleteButton}>
                                    <T phrase="Cancel"/>
                                </Button>
                                {paymentType !== -1 && !error && (
                                    <Button
                                        onClick={() => {
                                            if (!isNexioValid) {
                                                logPaymentEvent('INVALID_FORM_SUBMISSION_ATTEMPTED', {
                                                    paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                                                    processStep,
                                                    isNexioValid,
                                                    memberHasAutomaticPayment,
                                                    paymentFrequency,
                                                    frameUrl
                                                }, WcvLogLevel.WARN);
                                            }
                                            onPaymentSubmit();
                                        }} variant="contained"
                                        className={classes.submitButton}
                                        disabled={!isNexioValid || (automaticPaymentsEnabled && memberHasAutomaticPayment && paymentFrequency === -1)}
                                    >
                                        <T phrase={processStep === 1 ? 'Next' : 'Submit'}/>
                                    </Button>
                                )}
                                {error && isRecoverableError && (
                                    <Button
                                        onClick={() => {
                                            // Additional safety check - should not reach here for payment processing
                                            if (paymentProcessingAttempted) {
                                                logPaymentEvent('BLOCKED_PAYMENT_PROCESSING_RETRY', {
                                                    processStep,
                                                    paymentProcessingAttempted,
                                                    sessionId: sessionId.current
                                                }, WcvLogLevel.ERROR);

                                                setError('Payment processing has already been attempted. Please refresh the page to start a new payment or contact support at 1-800-222-4432.');
                                                setIsRecoverableError(false);
                                                enqueueSnackbar('Retry blocked to prevent duplicate payments.', {variant: 'warning'});
                                                return;
                                            }

                                            const newRetryCount = retryAttempts + 1;
                                            logPaymentEvent('RETRY_AFTER_TIMEOUT', {
                                                previousError: error,
                                                processStep,
                                                paymentType: paymentType === PaymentType.CARD ? 'CARD' : 'ECHEQUE',
                                                retryAttempt: newRetryCount,
                                                maxRetries: 3,
                                                paymentProcessingAttempted
                                            });
                                            setRetryAttempts(newRetryCount);
                                            setError(undefined);
                                            setIsRecoverableError(false);
                                            onPaymentSubmit();
                                        }}
                                        variant="contained"
                                        className={classes.submitButton}
                                        style={{marginRight: 10}}
                                    >
                                        Try Again
                                    </Button>
                                )}
                                {process.env.REACT_APP_HOST_ENV === 'local' && processStep === 2 && !error && (
                                    <Button
                                        onClick={triggerManualTimeout}
                                        variant="outlined"
                                        size="small"
                                        style={{
                                            marginRight: 10,
                                            backgroundColor: 'orange',
                                            color: 'white',
                                            fontSize: '10px',
                                            padding: '4px 8px'
                                        }}
                                    >
                                        DEV: Trigger Timeout
                                    </Button>
                                )}
                            </CardActions>
                        </React.Fragment>

                    )}
                </LoadingOverlay>
            </Card>
        </Modal>
    );
};

export default PaymentCheckoutFrame;
